import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import ApiClient from '../utils/ApiClient';
import PriceListTable from '../components/PriceLists/PriceListTable';
import BulkUpdateModal from '../components/PriceLists/BulkUpdateModal';

export default function PriceLists() {
    const [priceListData, setPriceListData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(50);
    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

    // Load price list data
    const loadPriceListData = async (page = 0, search = '') => {
        try {
            setLoading(true);
            const response = await ApiClient.get('/api/pricelists', {
                params: {
                    page: page,
                    size: pageSize,
                    search: search || undefined
                }
            });
            
            setPriceListData(response.data.data || []);
            setTotalPages(response.data.totalPages || 0);
            setTotalCount(response.data.totalCount || 0);
            setCurrentPage(response.data.currentPage || 0);
        } catch (error) {
            console.error('Error loading price list data:', error);
            setPriceListData([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        loadPriceListData(0, searchTerm);
    }, []);

    // Handle search
    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        loadPriceListData(0, searchTerm);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            loadPriceListData(newPage, searchTerm);
        }
    };

    // Handle bulk update completion
    const handleBulkUpdateComplete = (result) => {
        // Show success message or handle result
        console.log('Bulk update completed:', result);
        
        // Refresh the data
        loadPriceListData(currentPage, searchTerm);
        
        // Close modal
        setShowBulkUpdateModal(false);
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Price Lists</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage vendor price lists, costs, and MAP pricing. Total records: {totalCount.toLocaleString()}
                    </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <button
                        type="button"
                        onClick={() => setShowBulkUpdateModal(true)}
                        className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <ArrowUpTrayIcon className="h-4 w-4 mr-2 inline" />
                        Bulk Update
                    </button>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mt-6">
                <form onSubmit={handleSearch} className="flex gap-x-4">
                    <div className="flex-1">
                        <label htmlFor="search" className="sr-only">
                            Search price lists
                        </label>
                        <div className="relative">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                                id="search"
                                name="search"
                                type="search"
                                placeholder="Search by UPC, item code, product title, or vendor..."
                                className="block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        Search
                    </button>
                </form>
            </div>

            {/* Price List Table */}
            <div className="mt-8">
                <PriceListTable 
                    data={priceListData}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* Bulk Update Modal */}
            {showBulkUpdateModal && (
                <BulkUpdateModal
                    open={showBulkUpdateModal}
                    onClose={() => setShowBulkUpdateModal(false)}
                    onComplete={handleBulkUpdateComplete}
                />
            )}
        </div>
    );
}